/**
 * Upgrade to Smart Dialog
 * 
 * Modal dialog that displays upgrade confirmation details
 * Shows current plan, new plan, and effective date
 * 
 * @package @ideoz/ui
 */

import React from 'react';
import { X, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DialogDescription,
  DialogClose,
  Skeleton,
} from '@ideoz/ui';

export interface UpgradePlanInfo {
  id?: string;
  name: string;
  price: number;
  billingPeriod: string;
}

export type UpgradeToSmartDialogVariant = 'billing' | 'project-limit';

export interface UpgradeToSmartDialogProps {
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  variant?: UpgradeToSmartDialogVariant;
  // Billing variant props
  currentPlan?: UpgradePlanInfo;
  newPlan?: UpgradePlanInfo;
  effectiveDate?: string;
  // Project limit variant props
  currentPlanName?: string;
  currentPlanPrice?: string;
  currentPlanProjects?: string;
  upgradedPlanName?: string;
  upgradedPlanPrice?: string;
}

export default function UpgradeToSmartDialog({
  isOpen,
  isLoading = false,
  onClose,
  onConfirm,
  variant = 'billing',
  // Billing variant props
  currentPlan,
  newPlan,
  effectiveDate,
  // Project limit variant props
  currentPlanName = 'Free',
  currentPlanPrice = '$0/month',
  currentPlanProjects = '1 Project',
  upgradedPlanName = 'Smart',
  upgradedPlanPrice = '$19/month',
}: UpgradeToSmartDialogProps): JSX.Element {
  // Features list for project-limit variant
  const features = [
    'Higher credit limits with Smart plan',
    'Unlimited projects',
    'Unlimited prototypes with iterative flows',
    'Update prototype design system',
    'Advanced context builder for richer designs',
    '+ more features',
  ];

  // Render billing variant (default)
  if (variant === 'billing') {
    if (!currentPlan || !newPlan || !effectiveDate) {
      console.warn('UpgradeToSmartDialog: billing variant requires currentPlan, newPlan, and effectiveDate');
      return <></>;
    }

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader className="flex items-center justify-between">
            <DialogTitle className="font-semibold text-[#1D2939]">
              Upgrade to {newPlan.name}
            </DialogTitle>
            <DialogClose
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#717680] transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogClose>
          </DialogHeader>

          <DialogDescription className="text-xs text-[#717680]">
            You're about to upgrade your plan to {newPlan.name}. Please review the details below before confirming your upgrade.
          </DialogDescription>

          <div className="space-y-2 border border-gray-200 rounded-sm p-2">
            {/* Plan Comparison */}
            <div className="space-y-3">
              {/* Current Plan */}
              <div className="flex items-center justify-between ">
                  <p className="text-xs text-[#414651]">Current plan</p>
                  <p className="text-xs text-[#252B37]">
                    {currentPlan.name} – ${currentPlan.price}/{currentPlan.billingPeriod}
                  </p>
              </div>

              {/* New Plan */}
              <div className="flex items-center justify-between ">
                  <p className="text-xs text-[#414651]">New plan</p>
                  <p className="text-xs text-[#252B37]">
                    {newPlan.name} – ${newPlan.price}/{newPlan.billingPeriod}
                  </p>
              </div>

              {/* Effective Date */}
              <div className="flex items-center justify-between">
                  <p className="text-xs text-[#414651]">Effective day</p>
                  <p className="text-xs text-[#252B37]">{effectiveDate}</p>
              </div>
            </div>
          </div>

            {/* Billing Info */}
              <p className="text-xs text-[#717680]">
                You'll be charged ${newPlan.price}/{newPlan.billingPeriod} today and your billing will renew {newPlan.billingPeriod === 'year' ? 'annually' : 'monthly'} on this date.
              </p>

          <DialogFooter className="flex">
            <Button
              variant="secondary-gray"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isLoading}
              variant="primary"
            >
              {isLoading ? 'Upgrading...' : 'Upgrade'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Render project-limit variant
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[500px] w-[500px] p-6">
        <DialogHeader className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Light green icon/spark */}
            <div className="w-4 h-4 rounded-sm bg-green-200 flex items-center justify-center">
             
            </div>
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Upgrade to Smart
            </DialogTitle>
          </div>
          <button
            onClick={onClose}
            className="dialog-close-button"
          >
            <X color="#667085" strokeWidth={2.5} />
          </button>
        </DialogHeader>

        {/* Descriptive text */}
        <p className="text-sm text-gray-700 leading-relaxed">
          Your plan is eligible for one project only, upgrade your plan to create more than one
          projects and accelerate your design work
        </p>

        {/* Plan Comparison Section */}
        <div className="space-y-4">
          {/* Current Plan */}
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Current plan</span>
            <span className="text-sm text-gray-900">
              {currentPlanName} - {currentPlanPrice}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Available projects</span>
            <span className="text-sm text-gray-900">{currentPlanProjects}</span>
          </div>

          {/* Upgraded Plan */}
          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
            <span className="text-sm font-medium text-gray-700">Upgraded plan</span>
            <span className="text-sm text-gray-900">
              {upgradedPlanName} - {upgradedPlanPrice}
            </span>
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-2 pt-2">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-2">
              <Check
                className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0"
                strokeWidth={2.5}
              />
              <span className="text-sm text-gray-700">{feature}</span>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <DialogFooter className="flex-row justify-end gap-3 pt-4">
          <Button
            variant="secondary-gray"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirm}
            disabled={isLoading}
            data-track="upgrade_cta"
          >
            {isLoading ? 'Upgrading...' : 'Upgrade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
